import { bindable, autoinject } from 'aurelia-framework';
import { AutocompleteProviderFactory, IAutocompleteProvider, AutocompleteItem } from './autocomplete-service';
import { take } from 'lodash';
import { KeyCode } from '../../utils/key-code';

@autoinject
export class Autocomplete {
	@bindable datasource: string|any[];

	@bindable value: string;
	@bindable selectedId: string;
	@bindable inputHasFocus: boolean;
	@bindable placeholderResource: string;
	@bindable filter: (params: any) => boolean;
	@bindable idKey: string = 'id';
	@bindable nameKey: string = 'name.fi';
	@bindable translateName: boolean = false;
	@bindable specifierKey: string = 'specifier.fi';
	@bindable urlKey: string = 'url';
	@bindable idRequired: boolean = false;
	@bindable size: string = 'normal';
	@bindable itemSelected: (item: any) => void;
	@bindable maxItems: number = 10;
	@bindable widthClass: string;
	@bindable inputId: string;

	items: AutocompleteItem[] = [];
	selectedIndex = 0;
	sizeClass: string = '';

	private previousValue: string = '';
	private valueIsDirty: boolean = false;

	private provider: IAutocompleteProvider;

	constructor(private providerFactory: AutocompleteProviderFactory) { }

	datasourceChanged() {
		this.updateDatasource();
	}

	bind() {
		this.updateDatasource();
	}

	updateDatasource() {
		this.provider = this.providerFactory.getProvider({
			dataSource: this.datasource,
			settings: {
				idKey: this.idKey,
				nameKey: this.nameKey,
				specifierKey: this.specifierKey,
				urlKey: this.urlKey,
				translateName: this.translateName
			}
		});
	}

	attached() {
		switch (this.size) {
			case 'normal':
				this.sizeClass = '';
				break;
			case 'large':
				this.sizeClass = 'input-lg';
				break;
			default:
				this.sizeClass = '';
				break;
		}
	}

	get hasItems() {
		return this.items.length > 0;
	}

	get focusedItem() {
		return this.items[this.selectedIndex];
	}

	selectedIdChanged(newValue: string, oldValue: string) {
		if (newValue === null) {
			this.value = null;
		}
	}

	get valueIsChanged() {
		return this.value !== this.previousValue;
	}

	search(event: KeyboardEvent) {
		if (this.isAutocompleteActionKey(event.keyCode)) {
			return;
		}

		if (!this.valueIsChanged) {
			return;
		}

		this.valueIsDirty = true;

		this.previousValue = this.value;

		if (this.value === '') {
			this.close();
			return;
		}

		this.provider.getItems(this.value)
			.then(items => {
				this.selectedIndex = 0;

				let resultItems: AutocompleteItem[];
				if (items && items.length > 0 && this.filter) {
					resultItems = items.filter(item => this.filter({ item }));
				} else {
					resultItems = items;
				}

				this.items = take(resultItems, this.maxItems);

				if (this.items.length > 0) {
					this.items[0].isActive = true;
				}
			});
	}

	private actionKeys = [
		KeyCode.Enter,
		KeyCode.Esc,
		KeyCode.UpArrow,
		KeyCode.DownArrow
	];

	isAutocompleteActionKey(keyCode: number) {
		return this.actionKeys.indexOf(keyCode) !== -1;
	}

	select(item: AutocompleteItem) {
		this.value = this.provider.renderName(item.name, item.specifier);
		this.previousValue = this.value;
		this.selectedId = item.id;
		this.items = [];
		this.valueIsDirty = false;

		if (this.itemSelected) {
			this.itemSelected({ item });
		}
	}

	checkKey(event: KeyboardEvent) {
		if (this.hasItems) {
			switch (event.keyCode) {
				case KeyCode.UpArrow:
					this.moveUp();
					break;
				case KeyCode.DownArrow:
					this.moveDown();
					break;
				case KeyCode.Enter:
					this.selectActive();
					return false;
				case KeyCode.Esc:
					this.close();
					break;
				default:
					break;
			}
		}

		return true;
	}

	moveUp() {
		if (this.selectedIndex === 0) {
			return;
		}

		this.focusedItem.isActive = false;
		this.selectedIndex--;
		this.focusedItem.isActive = true;
	}

	moveDown() {
		if (this.selectedIndex + 1 >= this.items.length) {
			return;
		}

		this.focusedItem.isActive = false;
		this.selectedIndex++;
		this.focusedItem.isActive = true;
	}

	selectActive() {
		this.select(this.focusedItem);
	}

	close() {
		this.items = [];
		if (this.idRequired && (this.selectedId == null || this.valueIsDirty)) {
			this.value = null;
		}
	}
}
